import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, Spinner } from 'react-bootstrap';
import { fetchPlans } from '../../features/subscription/plansSlice';
import { fetchOrganizationSubscription } from '../../features/subscription/subscriptionSlice';
import {
  confirmPaymentMethods,
  extractPaymentMethods,
  listPaymentMethods,
  subscribeToPlan,
  syncPaymentMethods,
  syncSubscriptionPlan,
} from '../../services/billingService';
import {
  buildDisplayPlanGroups,
  findPlanGroup,
  formatEnvelopeLabel,
  formatPriceParts,
  getPlanVariant,
  normalizeBillingInterval,
  sortPlanGroups,
} from '../../utils/plansCatalog';
import {
  formatAmountCents,
  getBillingErrorMessage,
  normalizePaymentFlowData,
  waitForPlanUpgrade,
} from '../../utils/billingPaymentFlow';
import { useStripeConfig } from './VolksignStripeProvider';
import PaymentMethodSelector from './PaymentMethodSelector';
import BillingPaymentModal from './BillingPaymentModal';

const PlanCheckoutPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { ready: stripeReady, error: stripeConfigError } = useStripeConfig();
  const { items, status } = useSelector((state) => state.plans);
  const userSubscription = useSelector((state) => state.subscription?.userSubscription);

  const planCode = String(searchParams.get('plan') || '').trim().toLowerCase();
  const billingInterval = normalizeBillingInterval(searchParams.get('interval') || 'month');

  const [submitting, setSubmitting] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [error, setError] = useState('');
  const [successNote, setSuccessNote] = useState('');

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState(null);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);

  const [paymentSelection, setPaymentSelection] = useState('saved');
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
  const [savePaymentMethod, setSavePaymentMethod] = useState(true);

  const [paymentModal, setPaymentModal] = useState({
    show: false,
    clientSecret: null,
    amountCents: null,
    currency: 'usd',
    paymentIntentId: null,
    title: 'Complete payment',
  });

  const appBase = `${window.location.origin}${process.env.PUBLIC_URL || ''}`;
  const billingReturnUrl = `${appBase}/settings/billing?subscribed=1&expected_plan=${encodeURIComponent(planCode)}`;

  useEffect(() => {
    if (status === 'idle') {
      dispatch(fetchPlans({ hideUnlimited: true, hideLegacy: true }));
    }
  }, [dispatch, status]);

  const loadPaymentMethods = useCallback(async () => {
    setLoadingPaymentMethods(true);
    try {
      let data = await listPaymentMethods();
      let { methods, defaultId } = extractPaymentMethods(data);
      if (defaultId && methods.length === 0) {
        data = await syncPaymentMethods();
        ({ methods, defaultId } = extractPaymentMethods(data));
      }
      setPaymentMethods(methods);
      setDefaultPaymentMethodId(defaultId);
      if (methods.length > 0) {
        setPaymentSelection('saved');
        const initial =
          methods.find((pm) => pm.id === defaultId || pm.is_default) ?? methods[0];
        setSelectedPaymentMethodId(initial?.id ?? null);
      } else {
        setPaymentSelection('new');
        setSelectedPaymentMethodId(null);
      }
    } catch {
      setPaymentMethods([]);
      setPaymentSelection('new');
    } finally {
      setLoadingPaymentMethods(false);
    }
  }, []);

  useEffect(() => {
    loadPaymentMethods();
  }, [loadPaymentMethods]);

  const planGroups = useMemo(
    () => sortPlanGroups(buildDisplayPlanGroups(items)),
    [items]
  );

  const group = useMemo(
    () => findPlanGroup(planGroups, planCode),
    [planGroups, planCode]
  );

  const selectedPlan = useMemo(
    () => getPlanVariant(group, billingInterval),
    [group, billingInterval]
  );

  const priceParts = formatPriceParts(selectedPlan, billingInterval);
  const isContact = selectedPlan?.isContactSales || priceParts.isContact;
  const isFree = selectedPlan?.price === 0;
  const isCurrent =
    selectedPlan?.isCurrent
    || (userSubscription?.planCode
      && String(userSubscription.planCode).toLowerCase() === planCode);

  const checkoutPlanCode = selectedPlan?.apiPlanCode ?? planCode;

  const finishUpgrade = useCallback(async (expectedCode) => {
    setUpgrading(true);
    setSuccessNote('Payment received. Updating your plan…');
    await waitForPlanUpgrade(expectedCode, {
      fetchSubscription: () => dispatch(fetchOrganizationSubscription()).unwrap(),
    });
    setUpgrading(false);
    navigate('/settings/billing?subscribed=1', { replace: true });
  }, [dispatch, navigate]);

  const openStripePaymentModal = useCallback((flowData) => {
    const amountLabel = formatAmountCents(flowData.amountCents, flowData.currency);
    const intervalLabel = billingInterval === 'year' ? 'year' : 'month';
    setPaymentModal({
      show: true,
      clientSecret: flowData.clientSecret,
      amountCents: flowData.amountCents,
      currency: flowData.currency,
      paymentIntentId: flowData.paymentIntentId,
      title: amountLabel
        ? `Pay ${amountLabel} / ${intervalLabel}`
        : `Pay for ${group?.name || selectedPlan?.name || 'plan'}`,
    });
  }, [billingInterval, group?.name, selectedPlan?.name]);

  const handleSubscribe = useCallback(async () => {
    if (!planCode || !selectedPlan) return;
    setSubmitting(true);
    setError('');
    setSuccessNote('');

    try {
      if (isContact) {
        setError('This plan requires custom pricing. Please contact sales.');
        return;
      }

      const useSaved = !isFree && paymentSelection === 'saved' && paymentMethods.length > 0;

      const response = await subscribeToPlan({
        planCode: checkoutPlanCode,
        billingInterval,
        paymentMethodSelection: isFree ? 'new' : (useSaved ? 'saved' : 'new'),
        paymentMethodId: useSaved ? selectedPaymentMethodId : undefined,
        useDefaultPaymentMethod:
          useSaved && !selectedPaymentMethodId && Boolean(defaultPaymentMethodId),
        savePaymentMethod: isFree ? false : (useSaved ? undefined : savePaymentMethod),
        setAsDefault: useSaved ? undefined : savePaymentMethod,
      });

      if (!response.success) {
        throw new Error(response.message || 'Subscription could not be started.');
      }

      const flow = normalizePaymentFlowData(response.data);

      if (flow.paymentCompleted) {
        if (!flow.planSynced) {
          try {
            await syncSubscriptionPlan();
          } catch {
            // Poll below still reconciles plan.
          }
        }
        await finishUpgrade(checkoutPlanCode);
        return;
      }

      if (flow.requiresPaymentUi && flow.clientSecret) {
        openStripePaymentModal(flow);
        return;
      }

      if (flow.requiresPaymentUi && !flow.clientSecret) {
        throw new Error(
          'Payment could not be started. Try a saved card or retry in a moment.'
        );
      }

      throw new Error('Unexpected subscription response. Please try again.');
    } catch (err) {
      setError(getBillingErrorMessage(err, 'Could not start checkout. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  }, [
    planCode,
    selectedPlan,
    checkoutPlanCode,
    billingInterval,
    isContact,
    isFree,
    paymentSelection,
    paymentMethods.length,
    selectedPaymentMethodId,
    defaultPaymentMethodId,
    savePaymentMethod,
    finishUpgrade,
    openStripePaymentModal,
  ]);

  const handlePaymentModalSuccess = async ({ paymentIntentId } = {}) => {
    const piId = paymentIntentId || paymentModal.paymentIntentId;
    try {
      if (piId) {
        await confirmPaymentMethods({
          paymentIntentId: piId,
          setAsDefault: savePaymentMethod,
        });
      }
    } catch {
      // Webhook may still apply; continue to poll.
    }
    setPaymentModal((prev) => ({ ...prev, show: false }));
    await finishUpgrade(checkoutPlanCode);
  };

  if (!planCode) {
    return (
      <Alert variant="warning">
        No plan selected.{' '}
        <Link to="/plans">Browse plans</Link>
      </Alert>
    );
  }

  if (status === 'loading' || (status === 'idle' && items.length === 0)) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (!group || !selectedPlan) {
    return (
      <Alert variant="warning">
        Plan &quot;{planCode}&quot; is not available for{' '}
        {billingInterval === 'year' ? 'annual' : 'monthly'} billing.{' '}
        <Link to="/plans">Choose another plan</Link>
      </Alert>
    );
  }

  const payDisabled =
    submitting
    || upgrading
    || isCurrent
    || isContact
    || !stripeReady
    || (paymentSelection === 'saved' && !selectedPaymentMethodId && !defaultPaymentMethodId);

  return (
    <div className="plan-checkout-page">
      <Button
        as={Link}
        to="/plans"
        variant="link"
        className="plan-checkout-page__back ps-0"
      >
        <i className="ri-arrow-left-line me-1" aria-hidden />
        All plans
      </Button>

      <Card className="shadow-sm plan-checkout-page__card">
        <Card.Body className="p-4 p-lg-5">
          <p className="text-muted small text-uppercase mb-1">Checkout</p>
          <h2 className="fw-semibold mb-1">{group.name || selectedPlan.name}</h2>
          <p className="text-muted">
            {billingInterval === 'year' ? 'Annual' : 'Monthly'} billing
          </p>

          <div className="plan-checkout-page__summary my-4">
            <div className="d-flex justify-content-between align-items-baseline">
              <span>Price</span>
              <strong className="fs-4">{priceParts.full || priceParts.display}</strong>
            </div>
            <div className="d-flex justify-content-between text-muted small mt-2">
              <span>Envelopes</span>
              <span>{formatEnvelopeLabel(group)}</span>
            </div>
          </div>

          {isCurrent && (
            <Alert variant="info">This is already your current plan.</Alert>
          )}

          {stripeConfigError && (
            <Alert variant="warning" className="small">{stripeConfigError}</Alert>
          )}

          {successNote && (
            <Alert variant="success" className="small">{successNote}</Alert>
          )}

          {error && <Alert variant="danger">{error}</Alert>}

          {!isFree && !isContact && (
            <>
              {loadingPaymentMethods ? (
                <div className="text-center py-2 mb-3">
                  <Spinner animation="border" size="sm" variant="primary" />
                </div>
              ) : (
                <PaymentMethodSelector
                  methods={paymentMethods}
                  defaultId={defaultPaymentMethodId}
                  selection={paymentSelection}
                  onSelectionChange={setPaymentSelection}
                  selectedPaymentMethodId={selectedPaymentMethodId}
                  onPaymentMethodIdChange={setSelectedPaymentMethodId}
                  savePaymentMethod={savePaymentMethod}
                  onSavePaymentMethodChange={setSavePaymentMethod}
                  disabled={submitting || upgrading}
                  idPrefix="plan-checkout"
                />
              )}
            </>
          )}

          <Button
            variant="primary"
            size="lg"
            className="w-100"
            disabled={payDisabled}
            onClick={handleSubscribe}
          >
            {submitting || upgrading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {upgrading ? 'Updating plan…' : 'Processing…'}
              </>
            ) : isContact ? (
              'Contact sales'
            ) : isFree ? (
              'Activate free plan'
            ) : (
              'Subscribe and pay'
            )}
          </Button>

          {!isFree && (
            <p className="text-muted small text-center mt-3 mb-0">
              Payment is processed securely with Stripe. Your plan updates after payment succeeds.
            </p>
          )}
        </Card.Body>
      </Card>

      <BillingPaymentModal
        show={paymentModal.show}
        onHide={() => setPaymentModal((prev) => ({ ...prev, show: false }))}
        title={paymentModal.title}
        amountCents={paymentModal.amountCents}
        currency={paymentModal.currency}
        clientSecret={paymentModal.clientSecret}
        returnUrl={billingReturnUrl}
        submitLabel="Pay and upgrade"
        onSuccess={handlePaymentModalSuccess}
        onError={(err) => setError(getBillingErrorMessage(err))}
      />
    </div>
  );
};

export default PlanCheckoutPage;
